import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { filter, map, switchMap, catchError } from 'rxjs/operators';
import { Observable, of } from 'rxjs';

import { MatSnackBar } from '@angular/material/snack-bar';

import { TaskType } from '../../../shared/enums';
import { environment } from '../../../../environments/environment';

import Response = ZoningAPI.Response;
import PaginatedResponse = ZoningAPI.PaginatedResponse;
import IAttachmentLink = ZoningAPI.IAttachmentLink;
import { InstanceService } from './instance.service';
import { BaseProcessService } from './base-process.service';
import { ITaskModel } from 'src/app/shared/interfaces/task-model.interface';
import ITask = ZoningAPI.ITask;
import ISearchProcessesPaginatedParams = ZoningAPI.ISearchProcessesPaginatedParams;

import * as _ from 'lodash';

const API_ENDPOINT = environment.ZONAR_API_URL + '/job';
const COMMON_API_ENDPOINT = environment.ZONAR_API_URL + '/common-data/jobs';

@Injectable({
    providedIn: 'root'
})
export class JobsService extends BaseProcessService {
    private readonly jobEndpoints = {
        [TaskType.CALCULATE_FOLIOS_MULTI_THREAD]: '/calculate/folios',
        [TaskType.GET_3D_TILES_DATA]: '/3d-tiles-data',
        [TaskType.GET_AUTOMATION_DATA_JOB]: '/automation-data',
        [TaskType.FILL_ZC_APPLICABLE_TYPOLOGIES]: '/fill-applicable-typologies',
        [TaskType.CALCULATE_ALL_TYPOLOGIES]: '/calculate/all-typologies',
        // [TaskType.IMPORT_NEW_PR_IDS]: '/import-plain-ids/property-record',
        // [TaskType.IMPORT_NEW_ZCV_IDS]: '/import-plain-ids/zc-version',
        // [TaskType.IMPORT_NEW_STREET_IDS]: '/import-plain-ids/street',
        [TaskType.RECALCULATE_LISTED_BY_STATUS_JOB]: '/recalculate-by-status',
        [TaskType.REMOVE_PROPERTY_RECORDS]: '/remove-property-records',
        [TaskType.REPLACE_LAND_USE_IDS]: '/replace-land-use-ids',
        [TaskType.COLLECT_PRS_ZONING_DATA]: '/collect-prs-zoning-data',
        [TaskType.COLLECT_PRS_OVERLAYS_DATA]: '/collect-prs-overlays-data',
        [TaskType.SYNC_ZONING_CODE_SOLR_DATA]: '/sync-zoning-code-solr-data',
        [TaskType.REMOVE_ZONING_CODE_SOLR_DATA]: '/remove-zoning-code-solr-data',
        [TaskType.COLLECT_PRS_MARKET_DATA]: '/collect-prs-market-data',
        [TaskType.REMOVE_ZCV_CRS_JOB]: '/remove-zcv-crs',
        [TaskType.REMOVE_ZONING_CODE_JOB]: '/remove-zoning-code',
        [TaskType.SYNC_ZC_PRS_JOB]: '/sync-zoning-code-property-records',
        [TaskType.REINDEX_MISSED_ZC_AU_JOB]: '/reindex-missed-zc-allowed-uses',
        [TaskType.FILL_BUILDING_MODEL_GEOMETRIES_JOB]: '/fill-building-model-geometries',
        [TaskType.CALCULATE_GROUP_IDS_WITH_UPDATED_PLACES_JOB]: '/calculate-group-ids-with-updated-places'
    };

    totalActiveJobsCount: number = 0;
    totalArchivedJobsCount: number = 0;

    constructor(
        protected readonly http: HttpClient,
        protected readonly snackBar: MatSnackBar,
        private readonly instanceService: InstanceService
    ) {
        super(http, snackBar);
        this.instanceService.currentInstance
            .pipe(
                filter(instance => Boolean(instance)),
                switchMap(() => this.fetchJobs())
            )
            .subscribe();
    }

    private addOrUpdateProcessWithData(
        jobData: ITaskModel,
        getArchived: boolean = false,
        updateOnly: boolean = false,
        searchParams?: ISearchProcessesPaginatedParams
    ): ITaskModel {
        const prSubject = getArchived ? this.archivedProcessesSubject : this.processesSubject;
        const existingJob = prSubject.value.find(job => job.id === jobData.id);
        if (!existingJob) {
            if (!updateOnly) {
                const jobs = this.getSortedJobs([...prSubject.value, jobData], searchParams);
                prSubject.next(jobs);
            }
            return jobData;
        } else {
            existingJob.initWithJSON(jobData);
            prSubject.next(prSubject.value.concat([]));
            return existingJob;
        }
    }

    private getSortedJobs(jobs: ITaskModel[], searchParams?: ISearchProcessesPaginatedParams) {
        jobs = _.sortBy(jobs, 'createdAt').reverse();
        if (_.get(searchParams, 'sortBy')) {
            jobs = _.sortBy(jobs, searchParams.sortBy);
            if (_.get(searchParams, 'sortOrder', 'desc') === 'desc') {
                jobs.reverse();
            }
        }
        return jobs;
    }

    addOrUpdateProcessesWithData(jobsData: ITask[]) {
        this.processesSubject.next(this.getUpdatedProcessesWithData(jobsData));
    }

    private getUpdatedProcessesWithData(jobsData: ITask[]) {
        const newJobs: ITaskModel[] = [];
        if (_.isEmpty(jobsData)) {
            return this.processesSubject.value;
        }
        for (const jobData of jobsData) {
            const existingJob = this.processesSubject.value.find(job => job.id === jobData.id);
            if (existingJob) {
                existingJob.initWithJSON(jobData);
            }
        }
        return newJobs.length ? [...this.processesSubject.value, ...newJobs] : this.processesSubject.value;
    }

    removeJob(jobData: ITask) {
        if (!jobData) {
            return;
        }

        this.processesSubject.next(this.processesSubject.value.filter(job => job.id !== jobData.id));
        this.archivedProcessesSubject.next(this.archivedProcessesSubject.value.filter(job => job.id !== jobData.id));
    }

    fetchJobsFromAPI(
        api: string,
        searchParams?: ISearchProcessesPaginatedParams,
        getArchived: boolean = false,
        page: number = 1,
        limit?: number
    ) {
        const parameters: any = {
            getCount: 'true',
            page: String(page),
            getArchived: String(getArchived)
        };

        if (limit) {
            parameters.limit = String(limit);
        }

        if (searchParams) {
            ['sortBy', 'sortOrder', 'createdAt', 'name', 'statuses', 'type', 'state'].forEach(paramName => {
                if (searchParams[paramName]) {
                    parameters[paramName] = searchParams[paramName];
                }
            });
        }

        return this.fetchJobsByParams(getArchived, api, parameters);
    }

    fetchJobs(
        searchParams?: ISearchProcessesPaginatedParams,
        getArchived: boolean = false,
        page: number = 1,
        limit?: number
    ) {
        return this.fetchJobsFromAPI(API_ENDPOINT, searchParams, getArchived, page, limit);
    }

    fetchAllJobs(
        searchParams?: ISearchProcessesPaginatedParams,
        getArchived: boolean = false,
        page: number = 1,
        limit?: number
    ): Observable<ITaskModel[]> {
        return this.fetchJobsFromAPI(COMMON_API_ENDPOINT, searchParams, getArchived, page, limit);
    }

    fetchJobsByParams(getArchived: boolean, url: string, fromObject) {
        return this.http
            .get<PaginatedResponse<ITask[]>>(url, {
                params: new HttpParams({
                    fromObject
                })
            })
            .pipe(
                map(response => {
                    return this.initAndCountEntities(response, getArchived);
                }),
                map(jobs => {
                    this.clearJobs(getArchived);
                    return jobs.map(job =>
                        this.addOrUpdateProcessWithData(
                            job,
                            getArchived,
                            false,
                            fromObject as ISearchProcessesPaginatedParams
                        )
                    );
                })
            );
    }

    addJob(type: TaskType, formData: any) {
        const headers = new HttpHeaders();
        headers.set('Content-Type', null);
        headers.set('Accept', 'multipart/form-data');
        return this.http
            .post<Response>(API_ENDPOINT + this.jobEndpoints[type], formData, { responseType: 'json', headers })
            .pipe(
                catchError(error => {
                    this.snackBar.open(error.error.messages[0], 'OK', {
                        verticalPosition: 'top',
                        horizontalPosition: 'center',
                        duration: 4000
                    });
                    return of(null);
                }),
                filter(response => response !== null),
                map(response => this.initEntity(response.data))
            );
    }

    continueJob(job: ITaskModel) {
        return this.continueProcess(job, true)
            .pipe(map(response => this.initEntity(response)))
            .subscribe(jobData => this.addOrUpdateProcessWithData(jobData, undefined, true));
    }

    stopJob(job: ITaskModel, makeIdle?: boolean) {
        return this.stopProcess(job, true, makeIdle)
            .pipe(map(response => this.initEntity(response)))
            .subscribe(jobData => this.addOrUpdateProcessWithData(jobData, undefined, true));
    }

    getJobAttachments(job: ITaskModel) {
        return this.http
            .get<Response>(`${API_ENDPOINT}/${job.id}/attachments?state_env=${job.state}`)
            .pipe(map(response => response.data as IAttachmentLink[]))
            .subscribe(response => {
                const existingJob = this.processesSubject.value.find(j => j.id === job.id);
                existingJob.initAttachments(response);
                this.processesSubject.next(this.processesSubject.value);
            });
    }

    deleteJob(job: ITaskModel) {
        return this.deleteProcess(job, true)
            .pipe(map(response => this.initEntity(response)))
            .subscribe();
    }

    checkJobStatus(job: ITaskModel, skipStatistic: boolean = false) {
        return this.uploadProcessById(job, skipStatistic)
            .pipe(map(response => this.initEntity(response)))
            .subscribe(jobData => {
                this.addOrUpdateProcessWithData(jobData, undefined, true);
                this.processesSubject.next(this.processesSubject.value);
            });
    }

    clearJobs(clearArchived: boolean) {
        if (clearArchived) {
            this.archivedProcessesSubject.next([]);
        } else {
            this.processesSubject.next([]);
        }
    }

    initAndCountEntities(response: PaginatedResponse<ITask[]>, getArchived: boolean) {
        if (getArchived) {
            this.totalArchivedJobsCount = response.rows;
        } else {
            this.totalActiveJobsCount = response.rows;
        }
        return this.initEntities(response.data);
    }

    processNewProcess(processData: ITask) {
        if (!processData) {
            return;
        }
        this.notifyProcessListChanged();
    }
}
